/**
 * Kartica »Temeljnica meseca« na strani Uvoz (docs/ARHITEKTURA.md, 15.4):
 * stanje delitve amortizacije po sredstvih (knjizeno proti cilju po
 * odstotkih) in prenos temeljnice za uvoz v program. Samo administrator.
 *
 * Postopek meseca: uvoz glavne knjige z amortizacijo, prenos temeljnice,
 * uvoz v program, ponovni uvoz glavne knjige; nato so sredstva »v redu«.
 */
import type { PregledTemeljnice, SredstvoDelitve, StatusDelitve, ZnesekPoNosilcu } from '@koda/shared';
import { useCallback, useEffect, useRef, useState, type JSX } from 'react';

import { api } from '../lib/api';
import { datum, znesek } from '../lib/format';
import { OpozorilaUvoza } from './OpozorilaUvoza';

interface Lastnosti {
  podjetje: string;
  /**
   * Stevec sprememb podatkov podjetja (npr. uvoz glavne knjige). Ob spremembi
   * kartica izbrani mesec prebere znova: po ponovnem uvozu s knjizeno
   * temeljnico morajo sredstva pokazati »v redu«.
   */
  osvezitev?: number;
}

const NAZIV_STATUSA: Record<StatusDelitve, string> = {
  ok: 'v redu',
  temeljnica: 'potrebna temeljnica',
  brez_navodila: 'brez odstotkov',
  brez_amortizacije: 'brez amortizacije',
  preveri: 'preveri',
};

const VRSTNI_RED: StatusDelitve[] = ['temeljnica', 'preveri', 'ok', 'brez_navodila', 'brez_amortizacije'];

/** Zneski po nosilcih v eni celici: »RAZ-A 60,00 · brez nosilca 10,00«. */
export function poNosilcih(zneski: ZnesekPoNosilcu[]): string {
  if (zneski.length === 0) return '—';
  return zneski.map((z) => `${z.nosilec === '' ? 'brez nosilca' : z.nosilec} ${znesek(z.znesek)}`).join(' · ');
}

/** Mesec `llll-mm` v zapis 09/2026. */
const mesecZaPrikaz = (mesec: string): string => {
  const [leto, m] = mesec.split('-');
  return `${m ?? ''}/${leto ?? ''}`;
};

export function TemeljnicaMeseca({ podjetje, osvezitev = 0 }: Lastnosti): JSX.Element {
  const [pregled, setPregled] = useState<PregledTemeljnice | null>(null);
  const [napaka, setNapaka] = useState<string | null>(null);
  const [odprto, setOdprto] = useState<string | null>(null);

  const zadnjeBranje = useRef(0);
  const osvezi = useCallback(
    (mesec: string | undefined): void => {
      const branje = ++zadnjeBranje.current;
      api
        .temeljnicaAmortizacije(podjetje, mesec)
        .then((p) => {
          if (branje !== zadnjeBranje.current) return;
          setPregled(p);
          setNapaka(null);
        })
        .catch((e: unknown) => {
          if (branje === zadnjeBranje.current) setNapaka(e instanceof Error ? e.message : 'Stanja delitve ni bilo mogoče prebrati.');
        });
    },
    [podjetje],
  );

  useEffect(() => {
    osvezi(undefined);
  }, [osvezi]);

  // Po spremembi podatkov se prebere isti mesec, ki je izbran (ne privzeti).
  const izbraniMesec = useRef<string | undefined>(undefined);
  izbraniMesec.current = pregled?.mesec ?? undefined;
  const prvaOsvezitev = useRef(osvezitev);
  useEffect(() => {
    if (osvezitev === prvaOsvezitev.current) return;
    prvaOsvezitev.current = osvezitev;
    osvezi(izbraniMesec.current);
  }, [osvezitev, osvezi]);

  const kljuc = (s: SredstvoDelitve): string => `${s.sredstvo}|${s.konto}`;
  const sredstva = [...(pregled?.sredstva ?? [])].sort((a, b) => VRSTNI_RED.indexOf(a.status) - VRSTNI_RED.indexOf(b.status));

  return (
    <section className="kartica" aria-labelledby="temeljnica-meseca">
      <div className="glava-kartice">
        <h2 id="temeljnica-meseca">Temeljnica meseca</h2>
      </div>
      <p className="podnaslov">
        Primerja amortizacijo, knjiženo po stroškovnih nosilcih, z razdelitvijo po odstotkih projektov in pripravi
        temeljnico za uvoz v program (storno knjiženega in nova razdelitev, vsota 0). Postopek: uvozi glavno knjigo
        z amortizacijo meseca, prenesi temeljnico, uvozi jo v program, nato glavno knjigo uvozi znova – sredstva so
        potem »v redu«.
      </p>
      <label>
        Mesec{' '}
        <input
          type="month"
          aria-label="Mesec temeljnice"
          value={pregled?.mesec ?? ''}
          onChange={(d) => {
            if (d.target.value !== '') {
              setOdprto(null);
              osvezi(d.target.value);
            }
          }}
        />
      </label>

      {napaka !== null && (
        <p className="napaka" role="alert">
          {napaka}
        </p>
      )}
      {pregled !== null && pregled.mesec === null && (
        <p className="drobno">Ni še naloženega registra osnovnih sredstev; izberi mesec.</p>
      )}
      {pregled !== null && pregled.opozorila.length > 0 && <OpozorilaUvoza opozorila={pregled.opozorila} />}

      {pregled !== null && pregled.mesec !== null && pregled.nastavljeno && (
        <>
          <p>
            {VRSTNI_RED.filter((s) => pregled.povzetek[s] > 0).map((s) => (
              <span key={s} className={s === 'temeljnica' || s === 'preveri' ? 'opozorilo' : 'drobno'} style={{ marginRight: '1rem' }}>
                {NAZIV_STATUSA[s]}: <strong>{pregled.povzetek[s]}</strong>
              </span>
            ))}
          </p>
          {pregled.vrstice.length > 0 ? (
            <p>
              <a className="gumb" href={api.naslovTemeljnice(podjetje, pregled.mesec)} download>
                Prenesi temeljnico {mesecZaPrikaz(pregled.mesec)} (.xlsx, {String(pregled.vrstice.length)} vrstic)
              </a>
            </p>
          ) : (
            sredstva.length > 0 && <p className="drobno">Za ta mesec ni razlik; temeljnica ni potrebna.</p>
          )}
        </>
      )}

      {sredstva.length > 0 && (
        <div className="tabela-ovoj">
          <table>
            <thead>
              <tr>
                <th>sredstvo</th>
                <th>konto</th>
                <th>stanje</th>
                <th className="stevilka">amortizacija</th>
                <th>knjiženo</th>
                <th>po odstotkih</th>
              </tr>
            </thead>
            <tbody>
              {sredstva.map((s) => (
                <tr key={kljuc(s)}>
                  <td>
                    <code>{s.sredstvo}</code> {s.naziv ?? ''}
                  </td>
                  <td>{s.konto === '' ? '—' : s.konto}</td>
                  <td className={s.status === 'temeljnica' || s.status === 'preveri' ? 'opozorilo' : undefined}>
                    {NAZIV_STATUSA[s.status]}
                    {s.razlog !== null && <span className="drobno"> – {s.razlog}</span>}
                  </td>
                  <td className="stevilka">
                    {s.knjizbe.length === 0 ? (
                      znesek(s.amortizacija)
                    ) : (
                      <button
                        type="button"
                        className="gumb-besedilo"
                        aria-expanded={odprto === kljuc(s)}
                        aria-label={`Knjižbe ${s.sredstvo}`}
                        onClick={() => {
                          setOdprto(odprto === kljuc(s) ? null : kljuc(s));
                        }}
                      >
                        {znesek(s.amortizacija)}
                      </button>
                    )}
                  </td>
                  <td>{poNosilcih(s.knjizeno)}</td>
                  <td>{s.status === 'brez_navodila' ? '—' : poNosilcih(s.cilj)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {odprto !== null && (
        <div className="tabela-ovoj" style={{ marginTop: '0.75rem' }}>
          <table aria-label="Knjižbe sredstva">
            <thead>
              <tr>
                <th>datum</th>
                <th>konto</th>
                <th>vrsta</th>
                <th>dokument</th>
                <th>nosilec</th>
                <th className="stevilka">znesek</th>
                <th>opomba</th>
              </tr>
            </thead>
            <tbody>
              {(sredstva.find((s) => kljuc(s) === odprto)?.knjizbe ?? []).map((k) => (
                <tr key={k.id}>
                  <td>{datum(k.datum)}</td>
                  <td>{k.konto}</td>
                  <td>{k.vrsta ?? '—'}</td>
                  <td>{k.dokument ?? '—'}</td>
                  <td>{k.nosilec === '' ? '—' : k.nosilec}</td>
                  <td className="stevilka">{znesek(k.znesek)}</td>
                  <td>{k.opomba ?? ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
